import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { Button, FormControl, MenuItem, Select, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FlatIcon, { FlatIconOptions } from 'src/components/ui/FlatIcon';
import InputSelect from 'src/components/ui/InputSelect';
import { usePagination } from 'src/context/pagination-context';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const totalPages = Math.ceil(count / rowsPerPage);

  const containerRef = useRef(null);
  const [minWidth, setMinWidth] = useState(0);

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, totalPages - 1);
  };

  const handlePageButtonClick = (event, newPage) => {
    onPageChange(event, newPage);
  };

  const renderPageButtons = () => {
    const buttons = [];
    const maxButtons = 3;
    let startPage = Math.max(0, page - Math.floor(maxButtons / 2));
    let endPage = startPage + maxButtons - 1;

    if (endPage >= totalPages) {
      endPage = totalPages - 1;
      startPage = Math.max(0, endPage - maxButtons + 1);
    }

    if (startPage > 0) {
      buttons.push(
        <Button
          key={0}
          variant={page === 0 ? 'contained' : 'outlined'}
          sx={{
            color: page === 0 ? 'white' : 'gray',
            borderRadius: 9999,
            borderWidth: 0,
            padding: 0,
            minWidth: '30px',
            maxWidth: '30px',
            minHeight: '30px',
            maxHeight: '30px',
            mx: 0.5,
          }}
          onClick={(e) => handlePageButtonClick(e, 0)}>
          1
        </Button>
      );
      if (startPage > 1) {
        buttons.push(
          <Typography
            key='start-ellipsis'
            sx={{ mx: 0.5 }}>
            ...
          </Typography>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={page === i ? 'contained' : 'outlined'}
          sx={{
            color: page === i ? 'white' : 'gray',
            borderRadius: 9999,
            borderWidth: 0,
            padding: 0,
            minWidth: '30px',
            maxWidth: '30px',
            minHeight: '30px',
            maxHeight: '30px',
            mx: 0.5,
          }}
          onClick={(e) => handlePageButtonClick(e, i)}>
          {i + 1}
        </Button>
      );
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        buttons.push(
          <Typography
            key='end-ellipsis'
            sx={{ mx: 0.5 }}>
            ...
          </Typography>
        );
      }
      buttons.push(
        <Button
          key={totalPages - 1}
          variant={page === totalPages - 1 ? 'contained' : 'outlined'}
          sx={{
            color: page === totalPages - 1 ? 'white' : 'gray',
            borderRadius: 9999,
            borderWidth: 0,
            padding: 0,
            minWidth: '30px',
            maxWidth: '30px',
            minHeight: '30px',
            maxHeight: '30px',
            mx: 0.5,
          }}
          onClick={(e) => handlePageButtonClick(e, totalPages - 1)}>
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        minWidth: minWidth || 'auto',
      }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='first page'>
        {theme.direction === 'rtl' ?
          <FlatIcon icon={FlatIconOptions.last} />
        : <FlatIcon icon={FlatIconOptions.first} />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label='previous page'>
        {theme.direction === 'rtl' ?
          <FlatIcon icon={FlatIconOptions.next} />
        : <FlatIcon icon={FlatIconOptions.back} />}
      </IconButton>
      {renderPageButtons()}
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= totalPages - 1}
        aria-label='next page'>
        {theme.direction === 'rtl' ?
          <FlatIcon icon={FlatIconOptions.back} />
        : <FlatIcon icon={FlatIconOptions.next} />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= totalPages - 1}
        aria-label='last page'>
        {theme.direction === 'rtl' ?
          <FlatIcon icon={FlatIconOptions.first} />
        : <FlatIcon icon={FlatIconOptions.last} />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function CustomPaginationActionsTable({
  count,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  useContextPagination = true,
  rowsPerPageList = [
    { label: '25', value: 25 },
    { label: '50', value: 50 },
  ],
}) {
  const currentSizeWidth = window.screen.width;
  const getLabelTotal = () => {
    const firstTextLabel = `${Math.max(0, Number(page ?? 0) * Number(rowsPerPage ?? 0) + 1)}`;
    const secondTextLabel = `${Math.min((Number(page ?? 0) + 1) * Number(rowsPerPage ?? 0), Number(count ?? 0))}`;

    const totalTextLabel = `${Math.max(0, Number(count ?? 0))}`;

    return `${totalTextLabel == 0 ? 0 : firstTextLabel} - ${
      totalTextLabel == 0 ? 0 : secondTextLabel
    } de ${totalTextLabel}`;
  };

  const location = useLocation();
  const { setPage } = usePagination();
  const paginationKey = location.pathname;

  const handlePaginationChange = (newPage) => {
    if (useContextPagination) {
      setPage(paginationKey, newPage);
    }
    handleChangePage(newPage);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: currentSizeWidth < 768 ? 'space-around' : 'flex-end',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: currentSizeWidth < 768 ? 'space-around' : 'center',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Typography
            variant='body2'
            sx={{
              marginRight: 1,
              display: currentSizeWidth < 768 ? 'none' : 'inherit',
            }}>
            Linhas por página:
          </Typography>
          <InputSelect
            value={Number(rowsPerPage ?? 0)}
            onChange={(event) => handleChangeRowsPerPage(Number(event.target.value ?? 0))}
            values={rowsPerPageList}
            formStyle={{ width: 'auto', height: '32px' }}
            selectStyle={{ height: '32px' }}
            hideEmpty={true}
            disabled={rowsPerPageList.length > 1 ? false : true}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginX: '24px',
            display: currentSizeWidth < 768 ? 'none' : 'inherit',
          }}>
          <Typography
            variant='body2'
            sx={{
              color: 'gray',
            }}>
            {getLabelTotal()}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <TablePaginationActions
          key={'paging'}
          count={Number(count ?? 0)}
          page={Number(page ?? 0)}
          onPageChange={(_, newPage) => handlePaginationChange(Number(newPage ?? 0))}
          rowsPerPage={Number(rowsPerPage ?? 0)}
        />
      </Box>
    </Box>
  );
}
